import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { PrivateRoute, AdminRoute } from '../components/auth';
import Navbar from '../components/layout/Navbar';

// Import pages
import LandingPage from '../pages/LandingPage';
import AdminLogin from '../pages/AdminLogin';
import AssociateLogin from '../pages/AssociateLogin';
import DependentSetPassword from '../pages/DependentSetPassword';
import PublicRegister from '../pages/PublicRegister';
import EmailVerification from '../pages/EmailVerification';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import AssociatePartnersView from '../pages/AssociatePartnersView';
import CardValidation from '../pages/CardValidation';
import PendingApprovals from '../pages/PendingApprovals';
import Dashboard from '../pages/Dashboard';
import { Partners } from '../pages/Partners';
import { PartnerForm } from '../pages/PartnerForm';
import Categories from '../pages/Categories';
import { Associates } from '../pages/Associates';
import { AssociateForm } from '../pages/AssociateForm';
import AssociatePlans from '../pages/AssociatePlans';
import AssociateDependents from '../pages/AssociateDependents';
import Plans from '../pages/Plans';
import PlanForm from '../pages/PlanForm';
import { UseBenefit } from '../pages/UseBenefit';
import { Financial } from '../pages/Financial';
import { Receivables, MyFinancial } from '../pages';
import Settings from '../pages/Settings';

// Layout component for authenticated pages
const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main>{children}</main>
    </div>
  );
};

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/cadastro" element={<PublicRegister />} />
      <Route path="/verificar-email" element={<EmailVerification />} />
      {/* Validação pública do cartão virtual (aberta pelo QR Code) */}
      <Route path="/validar-cartao/:hash/:signature" element={<CardValidation />} />

      {/* Admin Auth Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Associate Auth Routes */}
      <Route path="/associado/login" element={<AssociateLogin />} />
      <Route path="/associado/forgot-password" element={<ForgotPassword />} />
      <Route path="/associado/reset-password" element={<ResetPassword />} />

      {/* Dependent Auth Routes — login é o mesmo do associado (/associado/login);
          o backend identifica quem é titular ou dependente pelo e-mail/CPF. */}
      <Route path="/dependente/login" element={<Navigate to="/associado/login" replace />} />
      <Route path="/dependente/criar-senha" element={<DependentSetPassword />} />
      <Route path="/dependente/forgot-password" element={<Navigate to="/associado/forgot-password" replace />} />
      <Route path="/dependente/reset-password" element={<Navigate to="/associado/reset-password" replace />} />

      {/* Associate Routes - Associates and Dependents */}
      <Route
        path="/meus-parceiros"
        element={
          <PrivateRoute allowedRoles={['associate', 'dependent']}>
            <AssociatePartnersView />
          </PrivateRoute>
        }
      />
      <Route
        path="/associado"
        element={<Navigate to="/meus-parceiros" replace />}
      />
      <Route
        path="/associado/home"
        element={<Navigate to="/meus-parceiros" replace />}
      />

      <Route
        path="/meu-historico-financeiro"
        element={
          <PrivateRoute allowedRoles={['associate', 'dependent']}>
            <MyFinancial />
          </PrivateRoute>
        }
      />

      {/* Admin Routes - Only for admins */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <Dashboard />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      {/* Partner Routes - Admin only */}
      <Route
        path="/parceiros"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <Partners />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/parceiros/novo"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <PartnerForm />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/parceiros/:id/editar"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <PartnerForm />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/categorias"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <Categories />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      {/* Plan Routes - Admin only */}
      <Route
        path="/planos"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <Plans />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/planos/novo"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <PlanForm />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/planos/:id/editar"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <PlanForm />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      {/* Associate Management Routes - Admin only */}
      <Route
        path="/associados"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <Associates />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/associados/aprovacoes"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <PendingApprovals />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/associados/novo"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <AssociateForm />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/associados/:id/editar"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <AssociateForm />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/associados/:id/planos"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <AssociatePlans />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/associados/:id/dependentes"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <AssociateDependents />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      {/* Benefit and Financial Routes - Admin only */}
      <Route
        path="/utilizar-beneficio"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <UseBenefit />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/financeiro"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <Financial />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/contas-receber"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AuthenticatedLayout>
              <Receivables />
            </AuthenticatedLayout>
          </PrivateRoute>
        }
      />

      {/* Settings - Admin only */}
      <Route
        path="/configuracoes"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AdminRoute>
              <AuthenticatedLayout>
                <Settings />
              </AuthenticatedLayout>
            </AdminRoute>
          </PrivateRoute>
        }
      />

      {/* Catch all - redirect to landing page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};
